/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { Component, Fragment } from 'react';

import { showError, showSuccess } from '../App/AppAlert';
import { showDialog } from '../App/AppDialog';
import { postJSON } from '../../support/Fetch';
import { formatDateTime } from '../../support/DateTime';
import VendorActions from '../../support/VendorActions';
import toProperName from '../../support/helpers/toProperName';
import calculatePrice from '../../support/shared/calculatePrice';
import ensureObject from '../../support/shared/ensureObject';
import {
  INVOICE_PROVIDERS,
  DEFAULT_INVOICE_PROVIDER,
} from '../../support/shared/constants/Constants';

import { Button, DropdownButton, Text, Link } from '../uiold';

import InvoiceConnect from '../Settings/InvoiceConnect';
import { getStatusText } from './helpers';

import IndexedList from '../../support/shared/IndexedList';
import { Market } from '../../types/shared/Market';
import { Vendor, Invoice } from '../../types/shared/Vendor';
import { Tier } from '../../types/shared/Tier';
import { Account } from '../../types/shared/Account';
import { Addon } from '../../types/shared/Addon';
import SignupFormModel from '../FormBuilder/SignupFormModel';
import { getOptionFields } from '../../support/helpers/CategoryOptions';

type Props = {
  account: Account;
  markets: IndexedList<Market>;
  vendor: Vendor;
  priceTiers: IndexedList<Tier>;
  addons: Map<string, Addon> | null | undefined;
  formDef: SignupFormModel;
};

type InvoiceLinkProps = {
  invoice: Invoice;
  createInvoice: () => void;
  removeInvoice: () => void;
};

export default class InvoiceControl extends Component<Props> {
  render() {
    let { account, vendor } = this.props;
    let { billing, invoice } = vendor;
    if (invoice) {
      return (
        <Fragment>
          <InvoiceMenuLink
            invoice={invoice}
            createInvoice={this._createInvoice}
            removeInvoice={this._showRemoveDialog}
          />
        </Fragment>
      );
    }
    return (
      <InvoiceConnect
        account={account}
        showBillingLink={!!billing}
        vendorId={vendor.id}
      >
        <Link onPress={this._createInvoice}>{t('Create Invoice')}</Link>
      </InvoiceConnect>
    );
  }

  _createInvoice = () => {
    let { account, markets, vendor, priceTiers, addons, formDef } = this.props;
    let { settings } = account;
    let invoiceProvider =
      (settings && settings.invoice_provider) || DEFAULT_INVOICE_PROVIDER;

    let isMetadataComplete =
      account.linked_accounts &&
      account.linked_accounts.quickbooks &&
      account.linked_accounts.quickbooks.metadata &&
      typeof account.linked_accounts.quickbooks.metadata.account_id ===
        'string' &&
      typeof account.linked_accounts.quickbooks.metadata
        .membership_fee_item_id === 'string' &&
      typeof account.linked_accounts.quickbooks.metadata
        .market_charges_item_id === 'string';

    if (
      // For QuickBooks, we must fetch the metadata first.
      invoiceProvider === INVOICE_PROVIDERS.quickbooks &&
      !isMetadataComplete
    ) {
      // TODO: Find alternative to disable create invoice when metadata is
      // incomplete. Add create invoice metadata here maybe?
      showError(
        t(
          'Unable to create invoice. Please disconnect and re-connect QuickBooks from Settings.',
        ),
      );
      return;
    }

    // TODO: This should be computed on the backend.
    const optionFields = getOptionFields(formDef);
    let { lineItems } = calculatePrice(
      markets,
      vendor.fields,
      priceTiers.toArray(),
      addons,
      optionFields,
    );

    let postData = {
      vendor_id: vendor.id,
      price: lineItems,
    };
    postJSON(`/${invoiceProvider}/invoices`, postData, (error, data) => {
      if (error) {
        console.error(error); // eslint-disable-line no-console
        let errorObject = ensureObject(error);
        let errorMessage = errorObject
          ? String(errorObject.message)
          : String(error);
        showError(
          t('Unable to complete request: {errorMessage}', { errorMessage }),
        );
        return;
      }
      if (data && data.success) {
        VendorActions.updateVendor(vendor.id, { invoice: data.invoice });
        data.isUpdated
          ? showSuccess(t('Invoice updated successfully.'))
          : showSuccess(t('Invoice created successfully.'));
      } else {
        let errorMessage =
          data != null && typeof data.error === 'string'
            ? data.error
            : t('unspecified error');
        showError(
          t('Unable to complete request: {errorMessage}', { errorMessage }),
        );
      }
    });
  };

  _showRemoveDialog = () => {
    let { vendor, account } = this.props;
    let linkedAccounts = account.linked_accounts || {};
    const CANCEL = t('Cancel');
    const REMOVE = t('Remove');
    let { invoice } = vendor;
    if (invoice) {
      let invoiceProvider = toProperName(invoice.provider || '');
      showDialog(
        t('Remove this invoice?'),
        invoice.provider && linkedAccounts[invoice.provider]
          ? t(
              'Are you sure you want to remove this invoice? This action will also remove this invoice on {invoiceProvider}.',
              { invoiceProvider },
            )
          : t(
              'Are you sure you want to remove this invoice? This action will not remove this invoice on {invoiceProvider} because you are disconnected from {invoiceProvider}.',
              { invoiceProvider },
            ),
        [CANCEL, REMOVE],
        (button) => {
          if (button === REMOVE) {
            this._removeInvoice();
          }
        },
      );
    }
  };

  _removeInvoice = () => {
    let { vendor } = this.props;
    let { invoice } = vendor;
    if (invoice && invoice.provider) {
      let invoiceProvider = invoice.provider;
      postJSON(
        `/${invoiceProvider}/invoices/${vendor.id}/remove`,
        {},
        (error, data) => {
          if (error) {
            console.error(error); // eslint-disable-line no-console
            let errorObject = ensureObject(error);
            let errorMessage = errorObject
              ? String(errorObject.message)
              : String(error);
            showError(
              t('Unable to complete request: {errorMessage}', { errorMessage }),
            );
            return;
          }
          if (data && data.success) {
            VendorActions.updateVendor(vendor.id, { invoice: null });
            showSuccess(t('Invoice removed successfully.'));
          } else {
            let errorMessage =
              data != null && typeof data.error === 'string'
                ? data.error
                : t('unspecified error');
            showError(
              t('Unable to complete request: {errorMessage}', { errorMessage }),
            );
          }
        },
      );
    }
  };
}

class InvoiceMenuLink extends Component<InvoiceLinkProps> {
  static VIEW_INVOICE = t('View Invoice');
  static UPDATE_INVOICE = t('Update Invoice');
  static REMOVE_INVOICE = t('Remove Invoice');

  render() {
    let { invoice } = this.props;
    let href = '';
    switch (invoice.provider) {
      case 'freshbooks':
        href = `https://my.freshbooks.com/#/invoice/${invoice.id}`;
        break;
      case 'quickbooks':
        href = `https://qbo.intuit.com/app/invoice?txnId=${invoice.id}`;
        break;
    }

    const invoiceMenu = [
      InvoiceMenuLink.VIEW_INVOICE,
      InvoiceMenuLink.UPDATE_INVOICE,
      InvoiceMenuLink.REMOVE_INVOICE,
    ];
    let invoiceMenuList = invoiceMenu.map((menuItem, index) => {
      return (
        <Button
          title={menuItem}
          key={index}
          onPress={() => this._onMenuChange(menuItem, href)}
          noBorder
        />
      );
    });

    return (
      <>
        <DropdownButton
          title={t('Invoice {invoiceNumber}', {
            invoiceNumber: invoice.number,
          })}
          showCaret
        >
          {invoiceMenuList}
        </DropdownButton>

        <Text type="label" style={{ marginTop: 4 }}>
          {t('created {createdAt} ({statusText})', {
            createdAt: formatDateTime(invoice.created_at),
            // TODO: Find a better way to handle statusText
            statusText: getStatusText(invoice.provider || '', invoice.status),
          })}
        </Text>
      </>
    );
  }

  _onMenuChange = (value: string, href: string) => {
    let { createInvoice, removeInvoice } = this.props;
    switch (value) {
      case InvoiceMenuLink.VIEW_INVOICE:
        window.open(href, '_blank');
        break;
      case InvoiceMenuLink.UPDATE_INVOICE:
        createInvoice();
        break;
      default:
        removeInvoice();
    }
  };
}
